import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Archive, ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, Eye, KeyRound, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import ProjectDetailsDialog from "../components/service-audit/ProjectDetailsDialog";
import ProjectFormDialog from "../components/service-audit/ProjectFormDialog";
import WeeklyAuditBoard from "../components/service-audit/WeeklyAuditBoard";
import WeeklyBackups from "../components/service-audit/WeeklyBackups";
import CodeSecurityReview from "../components/service-audit/CodeSecurityReview";
import CredentialsDialog from "../components/credentials/CredentialsDialog";
import { dateLabel, money, number, PROJECT_STATUSES } from "../components/service-audit/serviceAuditUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ServiceAudit() {
    const { hasPermission } = useAuth();
    const canViewFinancial = hasPermission("service_financial_view");
    const canManageFinancial = hasPermission("service_financial_manage");
    const canViewWeekly = hasPermission("service_weekly_audit_view");
    const canManageWeekly = hasPermission("service_weekly_audit_manage");
    const canViewBackups = hasPermission("service_backups_view");
    const canManageBackups = hasPermission("service_backups_manage");
    const canViewCodeSecurity = hasPermission("code_security_view");
    const canManageCodeSecurity = hasPermission("code_security_manage");
    const canViewCredentials = hasPermission("service_credentials_view");
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedWeek = searchParams.get("week") || "";
    const requestedCard = searchParams.get("card") || "";
    const initialTab = requestedWeek || requestedCard ? "weekly" : "financial";

    const [tab, setTab] = useState(initialTab);
    const [summary, setSummary] = useState(null);
    const [projects, setProjects] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(requestedWeek);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [formOpen, setFormOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [detailProjectId, setDetailProjectId] = useState(null);
    const [credentialsOpen, setCredentialsOpen] = useState(false);
    const [sort, setSort] = useState({ key: "deployment_code", direction: "asc" });

    const loadCore = useCallback(async () => {
        try {
            const [summaryRes, projectsRes, weeksRes, usersRes] = await Promise.all([
                canViewFinancial ? api.get("/service-audit/summary") : Promise.resolve({ data: null }),
                canViewFinancial ? api.get("/service-audit/projects") : Promise.resolve({ data: [] }),
                canViewWeekly ? api.get("/service-audit/weeks") : Promise.resolve({ data: [] }),
                api.get("/service-audit/users/options"),
            ]);
            setSummary(summaryRes.data);
            setProjects(projectsRes.data || []);
            setWeeks(weeksRes.data || []);
            setUsers(usersRes.data || []);
            if (canViewWeekly) setSelectedWeek((current) => current || weeksRes.data?.[0]?.week_key || "");
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    }, [canViewFinancial, canViewWeekly]);

    useEffect(() => { loadCore(); }, [loadCore]);
    useEffect(() => {
        if ((requestedWeek || requestedCard) && canViewWeekly) {
            setTab("weekly");
            if (requestedWeek) setSelectedWeek(requestedWeek);
        }
    }, [requestedWeek, requestedCard, canViewWeekly]);

    const visibleTabs = useMemo(() => [
        canViewFinancial && "financial",
        canViewWeekly && "weekly",
        canViewBackups && "backups",
        canViewCodeSecurity && "code-security",
    ].filter(Boolean), [canViewFinancial, canViewWeekly, canViewBackups, canViewCodeSecurity]);

    useEffect(() => {
        if (visibleTabs.length && !visibleTabs.includes(tab)) setTab(visibleTabs[0]);
    }, [visibleTabs, tab]);

    const filteredProjects = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = projects.filter((project) => {
            const matchesStatus = status === "all" || project.status === status;
            const matchesSearch = !term || [project.deployment_code, project.project_name, project.client_name, project.deployment_platform]
                .some((value) => String(value || "").toLowerCase().includes(term));
            return matchesStatus && matchesSearch;
        });
        return [...filtered].sort((a, b) => compareProjects(a, b, sort.key, sort.direction));
    }, [projects, search, status, sort]);

    const toggleSort = (key) => setSort((current) => ({
        key,
        direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

    const openNew = () => { setEditingProject(null); setFormOpen(true); };
    const openEdit = (project) => { setEditingProject(project); setFormOpen(true); };
    const archiveProject = async (project) => {
        if (!window.confirm(`¿Archivar ${project.deployment_code} · ${project.project_name}? Usa esta opción solo para registros erróneos. Para una baja real, edita el proyecto, agrega la fecha fin y cambia su estado a Finalizado.`)) return;
        try {
            await api.delete(`/service-audit/projects/${project.project_id}`);
            toast.success("Proyecto archivado");
            await loadCore();
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    };

    const changeTab = (value) => {
        setTab(value);
        if (value !== "weekly" && (requestedWeek || requestedCard)) setSearchParams({});
    };
    const changeWeek = (value) => {
        setSelectedWeek(value);
        if (tab === "weekly") setSearchParams({ week: value });
    };

    return (
        <Layout
            title="Auditoría de servicios"
            subtitle="Control financiero, auditoría funcional, backups y revisión técnica de proyectos en producción"
            actions={<div className="flex flex-wrap gap-2">
                {canViewCredentials && <Button variant="outline" onClick={() => setCredentialsOpen(true)}><KeyRound className="w-4 h-4 mr-2" />Credenciales</Button>}
                {canManageFinancial && <Button onClick={openNew}><Plus className="w-4 h-4 mr-2" />Registrar proyecto</Button>}
            </div>}
        >
            {canViewFinancial && <SummaryCards summary={summary} />}

            <Tabs value={tab} onValueChange={changeTab} className="mt-6">
                <TabsList className="flex h-auto w-full flex-wrap gap-1">
                    {canViewFinancial && <TabsTrigger className="flex-1 min-w-[180px]" value="financial">Control financiero</TabsTrigger>}
                    {canViewWeekly && <TabsTrigger className="flex-1 min-w-[180px]" value="weekly">Auditoría semanal</TabsTrigger>}
                    {canViewBackups && <TabsTrigger className="flex-1 min-w-[180px]" value="backups">BackUps Semanales</TabsTrigger>}
                    {canViewCodeSecurity && <TabsTrigger className="flex-1 min-w-[180px]" value="code-security">Código y seguridad</TabsTrigger>}
                </TabsList>

                {canViewFinancial && <TabsContent value="financial" className="mt-5 space-y-4">
                    <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between bg-white border border-slate-200 rounded-lg p-4">
                        <div className="relative flex-1 max-w-xl">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Buscar por ID, proyecto, cliente o plataforma…" />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-full lg:w-56"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="all">Todos los estados</SelectItem>{PROJECT_STATUSES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1550px] text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                                    <tr>
                                        <SortableTh sortKey="deployment_code" label="ID" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="project" label="Proyecto / cliente" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="status" label="Estado" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="deploy_start" label="Inicio deploy" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="months" label="Meses" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="credits" label="Créditos" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="credit_cost" label="Costo créditos" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="external_cost" label="Costo externo" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="total_cost" label="Total Int/Ext" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="renewal" label="Renovación" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="billing_frequency" label="Periodicidad" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="billing_amount" label="Monto facturación" sort={sort} onSort={toggleSort} />
                                        <SortableTh sortKey="billing_status" label="Estado facturación" sort={sort} onSort={toggleSort} />
                                        <Th className="text-right">Acciones</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProjects.map((project) => (
                                        <tr key={project.project_id} className="border-t border-slate-100 hover:bg-slate-50/70">
                                            <Td><span className="font-semibold text-orange-600">{project.deployment_code}</span></Td>
                                            <Td><button type="button" onClick={() => setDetailProjectId(project.project_id)} className="text-left"><p className="font-medium text-slate-900 hover:text-orange-600">{project.project_name}</p><p className="text-xs text-slate-500 mt-0.5">{project.client_name}</p></button>{project.production_url && <a href={project.production_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-600 mt-1 hover:underline"><ExternalLink className="w-3 h-3" />Producción</a>}</Td>
                                            <Td><StatusPill status={project.status} /></Td>
                                            <Td>{dateLabel(project.deploy_start)}</Td>
                                            <Td>{number(project.financials?.months_accumulated)}</Td>
                                            <Td>{number(project.financials?.credits_accumulated)}</Td>
                                            <Td>{money(project.financials?.credit_cost_usd)}</Td>
                                            <Td>{money(project.financials?.external_cost_usd)}</Td>
                                            <Td><span className="font-semibold text-slate-900">{money(project.financials?.total_internal_external_usd)}</span></Td>
                                            <Td>{dateLabel(project.next_renewal)}</Td>
                                            <Td>{project.billing_frequency || "Anual"}</Td>
                                            <Td>{project.billing_amount_usd == null ? "—" : money(project.billing_amount_usd)}</Td>
                                            <Td><BillingPill status={project.billing_status} /></Td>
                                            <Td><div className="flex items-center justify-end gap-1"><Button variant="ghost" size="icon" title="Ver detalle" onClick={() => setDetailProjectId(project.project_id)}><Eye className="w-4 h-4" /></Button>{canManageFinancial && <><Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(project)}><Pencil className="w-4 h-4" /></Button><Button variant="ghost" size="icon" title="Archivar registro erróneo" onClick={() => archiveProject(project)}><Archive className="w-4 h-4 text-red-500" /></Button></>}</div></Td>
                                        </tr>
                                    ))}
                                    {filteredProjects.length === 0 && <tr><td colSpan="14" className="py-16 text-center text-slate-400">No hay proyectos que coincidan con los filtros.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">Los registros archivados dejan de generar nuevas tarjetas semanales, pero sus semanas históricas permanecen intactas para auditoría.</p>
                </TabsContent>}

                {canViewWeekly && <TabsContent value="weekly" className="mt-5">
                    <WeeklyAuditBoard
                        weeks={weeks}
                        selectedWeek={selectedWeek}
                        onWeekChange={changeWeek}
                        users={users}
                        requestedCardId={requestedCard}
                        onRequestedCardHandled={() => setSearchParams(selectedWeek ? { week: selectedWeek } : {})}
                        onBoardChanged={loadCore}
                        canManage={canManageWeekly}
                    />
                </TabsContent>}

                {canViewBackups && <TabsContent value="backups" className="mt-5">
                    <WeeklyBackups canManage={canManageBackups} />
                </TabsContent>}

                {canViewCodeSecurity && <TabsContent value="code-security" className="mt-5">
                    <CodeSecurityReview canManage={canManageCodeSecurity} />
                </TabsContent>}
            </Tabs>

            {canManageFinancial && <ProjectFormDialog open={formOpen} onOpenChange={setFormOpen} project={editingProject} users={users} onSaved={loadCore} />}
            <ProjectDetailsDialog projectId={detailProjectId} open={Boolean(detailProjectId)} onOpenChange={(value) => !value && setDetailProjectId(null)} canManage={canManageFinancial} onEdit={(project) => { setDetailProjectId(null); openEdit(project); }} onChanged={loadCore} />
            <CredentialsDialog open={credentialsOpen} onOpenChange={setCredentialsOpen} scope="financial" />
        </Layout>
    );
}

function SummaryCards({ summary }) {
    const cards = [
        ["Proyectos activos", summary?.projects_active ?? "—"],
        ["Créditos acumulados", summary ? number(summary.credits_accumulated) : "—"],
        ["Costo créditos USD", summary ? money(summary.credit_cost_usd) : "—"],
        ["Costo externo USD", summary ? money(summary.external_cost_usd) : "—"],
        ["Costo Total Int/Ext", summary ? money(summary.total_internal_external_usd) : "—"],
        ["Renovaciones 60 días", summary?.upcoming_renewals_60_days ?? "—"],
        ["Facturas vencidas", summary?.overdue_invoices ?? "—"],
        ["Facturación anualizada", summary ? money(summary.billing_registered_annualized_usd ?? summary.annual_billing_registered_usd) : "—"],
    ];
    return <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{cards.map(([label, value], index) => <div key={label} className={`rounded-lg border p-4 ${index === 4 ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white"}`}><p className="text-xs text-slate-500">{label}</p><p className="text-xl font-semibold text-slate-900 mt-1">{value}</p></div>)}</div>;
}
function Th({ children, className = "" }) { return <th className={`px-4 py-3 text-left whitespace-nowrap ${className}`}>{children}</th>; }
function SortableTh({ sortKey, label, sort, onSort }) {
    const active = sort.key === sortKey;
    const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
    return (
        <th className="px-4 py-3 text-left whitespace-nowrap">
            <button type="button" onClick={() => onSort(sortKey)} className="inline-flex items-center gap-1 hover:text-orange-600 transition-colors">
                {label}<Icon className={`h-3.5 w-3.5 ${active ? "text-orange-500" : "text-slate-300"}`} />
            </button>
        </th>
    );
}
function projectSortValue(project, key) {
    const values = {
        deployment_code: project.deployment_code,
        project: `${project.project_name || ""} ${project.client_name || ""}`,
        status: project.status,
        deploy_start: project.deploy_start,
        months: project.financials?.months_accumulated,
        credits: project.financials?.credits_accumulated,
        credit_cost: project.financials?.credit_cost_usd,
        external_cost: project.financials?.external_cost_usd,
        total_cost: project.financials?.total_internal_external_usd,
        renewal: project.next_renewal,
        billing_frequency: project.billing_frequency || "Anual",
        billing_amount: project.billing_amount_usd,
        billing_status: project.billing_status,
    };
    return values[key];
}
function compareProjects(a, b, key, direction) {
    const av = projectSortValue(a, key);
    const bv = projectSortValue(b, key);
    const aEmpty = av === null || av === undefined || av === "";
    const bEmpty = bv === null || bv === undefined || bv === "";
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
    const numericKeys = new Set(["months", "credits", "credit_cost", "external_cost", "total_cost", "billing_amount"]);
    const dateKeys = new Set(["deploy_start", "renewal"]);
    let result = 0;
    if (numericKeys.has(key)) result = Number(av) - Number(bv);
    else if (dateKeys.has(key)) result = new Date(av).getTime() - new Date(bv).getTime();
    else result = String(av).localeCompare(String(bv), "es", { numeric: true, sensitivity: "base" });
    return direction === "asc" ? result : -result;
}
function Td({ children }) { return <td className="px-4 py-3 align-top text-slate-700 whitespace-nowrap">{children}</td>; }
function StatusPill({ status }) { const cls = status === "Activo" ? "bg-emerald-100 text-emerald-800" : status === "Suspendido" || status === "Finalizado" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"; return <span className={`text-xs px-2 py-1 rounded-full ${cls}`}>{status}</span>; }
function BillingPill({ status }) { const cls = status === "Vencida" ? "bg-red-100 text-red-800" : status === "Al día" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"; return <span className={`text-xs px-2 py-1 rounded-full ${cls}`}>{status || "Sin fecha"}</span>; }
